import { useState, useRef, useEffect, memo } from "react";
import { BACKEND_URL } from "@/lib/api";
import { optimizeImage } from "@/lib/mediaUrls";

const AudioPlayer = ({ 
  src, 
  trackId, 
  trackTitle = "Track", 
  artistName = "Artist",
  coverArt = null,
  onPlayCountIncrement = null,
  onEnded = null,
  autoPlay = false,
  compact = false 
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [hasCountedPlay, setHasCountedPlay] = useState(false);
  const [mediaError, setMediaError] = useState(null);  // Phase 15.6D
  const progressRef = useRef(null);

  // Track 10-second play threshold
  useEffect(() => {
    if (currentTime >= 10 && !hasCountedPlay && trackId && onPlayCountIncrement) {
      setHasCountedPlay(true);
      onPlayCountIncrement(trackId);
    }
  }, [currentTime, hasCountedPlay, trackId, onPlayCountIncrement]);

  // Reset play count tracking when track changes
  useEffect(() => {
    setHasCountedPlay(false);
    setCurrentTime(0);
    setIsPlaying(false);
    setMediaError(null);  // Phase 15.6D — reset error on track change
  }, [trackId, src]);

  // Phase 15.6D — surface playback errors with a clear user-facing message.
  // When the <audio> element fails to load (Cloudinary 401, network error,
  // or expired URL) we replace the silent broken player with an inline
  // notice so artists+listeners know to refresh / contact support.
  const handleAudioError = () => {
    setIsPlaying(false);
    setMediaError("playback_failed");
  };

  // Autoplay queue support: when `autoPlay` is true and the src/trackId
  // changes (queue advance), kick off playback automatically. Browsers
  // require playback to be initiated after a user gesture, so this only
  // succeeds when the user already started the previous track manually.
  useEffect(() => {
    if (!autoPlay) return;
    const el = audioRef.current;
    if (!el || !src) return;
    const tryPlay = async () => {
      try {
        await el.play();
        setIsPlaying(true);
      } catch (_e) {
        // Browser blocked autoplay; the play/pause button is still active.
      }
    };
    tryPlay();
  }, [autoPlay, src, trackId]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current && !isNaN(audioRef.current.duration) && audioRef.current.duration > 0) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleDurationChange = () => {
    if (audioRef.current && !isNaN(audioRef.current.duration) && audioRef.current.duration > 0) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleCanPlay = () => {
    if (audioRef.current && !isNaN(audioRef.current.duration) && audioRef.current.duration > 0) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleProgressClick = (e) => {
    if (progressRef.current && audioRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      audioRef.current.currentTime = percentage * duration;
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return "0:00";
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Determine full audio URL — strict mode. Only real remote URLs are allowed;
  // ephemeral /api/public/music/... endpoints are NOT persistent across
  // redeploys so we refuse to play from them (surfaces a clear "audio
  // unavailable" state instead of silently 404-ing mid-scroll).
  const audioUrl = src?.startsWith('http') ? src : '';
  const coverUrl = coverArt?.startsWith('http') ? coverArt : null;

  // Phase 15.6D — Unified "unavailable" surface. Two paths:
  //   • Empty src (DB missing music_url) → audio_missing
  //   • Audio element error event       → playback_failed (set by onError)
  const isUnavailable = !audioUrl || mediaError;
  const unavailableReason = !audioUrl ? "audio_missing" : mediaError;
  const unavailableMessage = !audioUrl
    ? "Audio unavailable — the artist may need to re-upload this track."
    : "Couldn't play this track right now. The media service may be temporarily down. Please try again in a few minutes.";

  if (compact) {
    if (isUnavailable) {
      return (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-200 text-xs" data-testid={`audio-unavailable-${trackId}`} data-unavailable-reason={unavailableReason}>
          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zm.78-3.05a.75.75 0 11-1.06-1.06 1 1 0 00-1.42-1.41 6 6 0 118.49-8.49 1 1 0 00-1.41-1.42L9.83 4.5a.75.75 0 11-1.06-1.06z"/></svg>
          <span className="flex-1 min-w-0 truncate">{unavailableMessage}</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-3" data-testid={`audio-player-${trackId}`}>
        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onDurationChange={handleDurationChange}
          onCanPlay={handleCanPlay}
          onError={handleAudioError}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => { setIsPlaying(false); if (onEnded) onEnded(); }}
          preload="metadata"
        />
        
        <button
          onClick={togglePlay}
          className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 flex items-center justify-center shadow-lg hover:shadow-purple-500/30 transition-all transform hover:scale-105 flex-shrink-0"
          data-testid="play-pause-btn"
        >
          {isPlaying ? (
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
            </svg>
          )}
        </button>

        <div className="flex-1 min-w-0">
          <div 
            ref={progressRef}
            onClick={handleProgressClick}
            className="h-1.5 bg-gray-700 rounded-full cursor-pointer group"
          >
            <div 
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full relative"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-900/80 to-black border border-purple-500/30 rounded-xl p-4" data-testid={`audio-player-${trackId}`}>
      {isUnavailable ? (
        <div
          className="flex items-start gap-3 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-100"
          data-testid={`audio-unavailable-${trackId}`}
          data-unavailable-reason={unavailableReason}
        >
          <svg className="w-6 h-6 text-rose-300 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm.78-3.05a.75.75 0 11-1.06-1.06 1 1 0 00-1.42-1.41 6 6 0 118.49-8.49 1 1 0 00-1.41-1.42L9.83 4.5a.75.75 0 11-1.06-1.06z" />
          </svg>
          <div className="flex-1 min-w-0">
            <p className="text-rose-100 font-semibold text-sm">Track unavailable</p>
            <p className="text-rose-200/80 text-xs mt-0.5">{unavailableMessage}</p>
            {mediaError && (
              <button
                onClick={() => { setMediaError(null); audioRef.current?.load?.(); }}
                className="mt-2 px-3 py-1 rounded bg-rose-500/30 hover:bg-rose-500/40 text-rose-100 text-xs font-semibold border border-rose-400/40"
                data-testid={`retry-audio-${trackId}`}
              >
                Retry
              </button>
            )}
          </div>
        </div>
      ) : (
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onDurationChange={handleDurationChange}
        onCanPlay={handleCanPlay}
        onError={handleAudioError}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => { setIsPlaying(false); if (onEnded) onEnded(); }}
        preload="metadata"
      />
      )}
      {!isUnavailable && (

      <div className="flex items-center gap-4">
        {/* Cover Art */}
        {coverUrl && (
          <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
            <img 
              src={optimizeImage(coverUrl, 'thumb')} 
              alt={trackTitle}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=100&h=100&fit=crop'; }}
            />
          </div>
        )}

        {/* Play Button */}
        <button
          onClick={togglePlay}
          className="w-14 h-14 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 flex items-center justify-center shadow-lg hover:shadow-purple-500/30 transition-all transform hover:scale-105 flex-shrink-0"
          data-testid="play-pause-btn"
        >
          {isPlaying ? (
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 20 20">
              <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
            </svg>
          )}
        </button>

        {/* Track Info + Progress */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <div className="min-w-0">
              <p className="text-white font-medium truncate">{trackTitle}</p>
              <p className="text-gray-400 text-sm truncate">{artistName}</p>
            </div>
            <span className="text-gray-500 text-sm ml-2">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          {/* Progress Bar */}
          <div 
            ref={progressRef}
            onClick={handleProgressClick}
            className="h-2 bg-gray-700 rounded-full cursor-pointer group"
          >
            <div 
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full relative transition-all"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
        </div>

        {/* Volume Control */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
          </svg>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={volume}
            onChange={handleVolumeChange}
            className="w-20 h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
            data-testid="volume-slider"
          />
        </div>
      </div>
      )}
    </div>
  );
};

export default memo(AudioPlayer, (prev, next) => {
  // Re-render ONLY if the audio source / track identity actually changes.
  // Parent re-renders triggered by unrelated state (likes, comments,
  // reactions, queue updates) MUST NOT disturb playback. This is the
  // safety net for "Like Button Must Not Restart Audio".
  return (
    prev.src === next.src &&
    prev.trackId === next.trackId &&
    prev.compact === next.compact &&
    prev.coverArt === next.coverArt &&
    prev.trackTitle === next.trackTitle &&
    prev.artistName === next.artistName &&
    prev.autoPlay === next.autoPlay &&
    prev.onEnded === next.onEnded
  );
});
